import React from 'react';
import styled from 'styled-components';
import RakutenLargeLogo from '../../../../../app/common/logos/RakutenLargeLogo';
import KoboLogo from '../../../../../app/common/logos/KoboLogo';
import RakutenTVLogo from '../../../../../app/common/logos/RakutenTVLogo';
import ViberLogo from '../../../../../app/common/logos/ViberLogo';

import { COLORS, SPACINGS, MQS } from '../../../../../app/common/constants/styles';
import { TranslationHelpers } from '../../../../../i18n/translationHelpers';
import { IHistoryEntry } from '../../redux/rewardsTransactionsHistory.service';
import { Row } from '../TransactionTable';
import Icon from '../../../../../app/common/components/Icon';
import ICONS from '../../../../../app/common/constants/icons';
import TransactionAccordionInfo from './TransactionAccordionInfo';
import Accordion from '../../../../../app/common/components/Accordion';
import { formatCashbackCurrency } from '../../../CurrencyFormatting';

const RewardType = styled.div`
    font-weight: 500;
    padding: ${SPACINGS.base}px 0;
    text-align: center;
    font-size: 0.8125rem;
    width: 30%;
    @media (min-width: ${MQS.mq2Min}) {
        width: 13%;
        margin-right: 1%;
        word-break: break-word;
    }

    @media (min-width: ${MQS.mq3Min}) {
        font-size: 0.9375rem;
    }
`;

const Date = styled.div`
    font-weight: normal;
    padding: ${SPACINGS.base}px 0;
    text-align: center;
    font-size: 0.8125rem;
    width: 30%;

    @media (min-width: ${MQS.mq2Min}) {
        width: 13%;
        margin-right: 1%;
        word-break: break-word;
    }
    @media (min-width: ${MQS.mq3Min}) {
        font-size: 0.9375rem;
    }
`;

const RegColumnText = styled.div`
    font-weight: normal;
    padding: ${SPACINGS.base}px 0;
    display: none;
    text-align: center;
    vertical-align: middle;
    font-size: 0.8125rem;
    width: 30%;

    @media (min-width: ${MQS.mq2Min}) {
        width: 13%;
        margin-right: 1%;
        display: inline-block;
        word-break: break-word;
    }
    @media (min-width: ${MQS.mq3Min}) {
        font-size: 0.875rem;
    }
`;

const RegColumnNumber = styled(RegColumnText)`
    @media (min-width: ${MQS.mq3Min}) {
        font-size: 0.9375rem;
    }
`;

const MultiLineColumnText = styled.div`
    font-weight: normal;
    padding: ${SPACINGS.base}px 0;
    text-align: center;
    vertical-align: middle;
    font-size: 0.6875rem;
    word-wrap: break-word;
    width: 30%;
    @media (min-width: ${MQS.mq2Min}) {
        width: 13%;
        margin-right: 1%;
        display: inline-block;
        word-break: break-word;
    }
    @media (min-width: ${MQS.mq3Min}) {
        font-size: 0.875rem;
    }
`;

const EarnedSpent = styled.div`
    font-weight: normal;
    color: ${COLORS.green};
    padding: ${SPACINGS.base}px 0;
    text-align: center;
    font-size: 0.8125rem;
    word-break: break-word;

    &.earned {
        color: ${COLORS.green};
    }
    &.spent {
        color: ${COLORS.rakutenRed};
    }
    display: none;
    width: 30%;

    @media (min-width: ${MQS.mq2Min}) {
        width: 13%;
        margin-right: 1%;
        display: block;
    }
    @media (min-width: ${MQS.mq3Min}) {
        font-size: 0.875rem;
    }
`;

const MoreInfoColumn = styled.div`
    font-weight: normal;
    padding: ${SPACINGS.base}px 0;
    display: block;
    text-align: center;
    svg {
        height: 15px;
        color: ${COLORS.grey};
        transform: rotate(90deg);
    }
    width: 10%;
    @media (min-width: ${MQS.mq2Min}) {
        width: 14%;
        display: none;
    }
`;

const Logo = styled.div`
    padding: ${SPACINGS.base}px 0;
    text-align: center;
    justify-content: center;

    width: 30%;
    display: none;

    @media (min-width: ${MQS.mq2Min}) {
        width: 13%;
        margin-right: 1%;
        display: inline-block;
        word-break: break-word;
    }

    span {
        line-height: 1.25;
    }
    svg {
        height: 18px;
    }

    &.logo-rakuten svg,
    &.logo-tv svg,
    &.logo-viber svg,
    &.logo-kobo svg {
        margin-left: 8%;
        width: 100%;
    }
`;

interface ITransactionRowProps {
    transaction: IHistoryEntry;
    rewardValue: number;
    type: string;
    pointsSelected: boolean;
    selectedLocale: string;
}

interface ITransactionRowState {
    showAccordion: boolean;
}

class Transaction extends React.Component<{
    transaction: IHistoryEntry;
    pointsSelected: boolean;
    selectedLocale: string;
}> {
    render() {
        const { transaction, pointsSelected, selectedLocale } = this.props;
        return (
            <div>
                {transaction.stdChange !== 0 ? (
                    <TransactionRow
                        transaction={transaction}
                        rewardValue={transaction.stdChange}
                        type={'standard'}
                        pointsSelected={pointsSelected}
                        selectedLocale={selectedLocale}
                    ></TransactionRow>
                ) : null}
                {transaction.termChange !== 0 ? (
                    <TransactionRow
                        transaction={transaction}
                        rewardValue={transaction.termChange}
                        type={'expiring'}
                        pointsSelected={pointsSelected}
                        selectedLocale={selectedLocale}
                    ></TransactionRow>
                ) : null}
            </div>
        );
    }
}

class TransactionRow extends React.Component<ITransactionRowProps, ITransactionRowState> {
    constructor(props: ITransactionRowProps) {
        super(props);
        this.state = {
            showAccordion: false,
        };
        this.showDetailsPopup = this.showDetailsPopup.bind(this);
    }

    private showDetailsPopup() {
        this.setState({
            showAccordion: !this.state.showAccordion,
        });
    }

    render() {
        const {
            transaction,
            transaction: { action, transactionTime, historyText, logo, orderNo },
            rewardValue,
            type,
            pointsSelected,
            selectedLocale,
        } = this.props;
        const { showAccordion } = this.state;
        return (
            <div>
                <Row>
                    {pointsSelected ? (
                        <RewardType>{rewardValue}</RewardType>
                    ) : (
                        <RewardType>
                            {formatCashbackCurrency(rewardValue, selectedLocale)}
                        </RewardType>
                    )}

                    {action === 'GRANT' ? (
                        <EarnedSpent className={'earned'}>
                            {TranslationHelpers.getFormattedMessage('points.earned')}
                        </EarnedSpent>
                    ) : (
                        <EarnedSpent className={'spent'}>
                            {TranslationHelpers.getFormattedMessage('points.spent')}
                        </EarnedSpent>
                    )}
                    <RegColumnText>
                        {type === 'standard'
                            ? TranslationHelpers.getFormattedMessage('points.standard')
                            : TranslationHelpers.getFormattedMessage('points.expiring')}
                    </RegColumnText>
                    <Date>{transactionTime}</Date>
                    <RegColumnNumber>{orderNo}</RegColumnNumber>
                    <MultiLineColumnText>{historyText}</MultiLineColumnText>
                    <Logo className={`logo-${logo || 'rakuten'}`}>
                        {logo === 'rakuten' ? (
                            <RakutenLargeLogo />
                        ) : logo === 'tv' ? (
                            <RakutenTVLogo />
                        ) : logo === 'viber' ? (
                            <ViberLogo />
                        ) : logo === 'kobo' ? (
                            <KoboLogo />
                        ) : (
                            <RakutenLargeLogo />
                        )}
                    </Logo>
                    <MoreInfoColumn onClick={this.showDetailsPopup}>
                        {' '}
                        <Icon icon={ICONS.ARROW_RIGHT} />
                    </MoreInfoColumn>
                </Row>
                {showAccordion && (
                    <Accordion isOpen={showAccordion}>
                        <TransactionAccordionInfo
                            transaction={transaction}
                            type={type}
                        ></TransactionAccordionInfo>
                    </Accordion>
                )}
            </div>
        );
    }
}

export default Transaction;
